'use client'

import * as React from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import { FreeMode, Mousewheel } from 'swiper/modules'
import { cn } from '../../helpers/index.js'
import { Heading, Button, Text } from '../../components/index.js'
import { useSceneShelfContext } from './context.js'
import type { PriceData, ButtonFunctionType } from './types.js'
import 'swiper/css'

export interface SceneShelfProductsPanelProps extends React.HTMLAttributes<HTMLDivElement> {
  /** 标题 */
  title?: string
  /** 总价标签 */
  totalPriceLabel?: string
  /** 当前总价 */
  totalCurrentPrice?: PriceData
  /** 原总价 */
  totalOriginalPrice?: PriceData
  /** 主要按钮文本 */
  primaryButtonText?: string
  /** 主要按钮功能类型 */
  primaryButtonFun?: ButtonFunctionType
  /** 次要按钮文本 */
  secondaryButtonText?: string
  /** 次要按钮功能类型 */
  secondaryButtonFun?: ButtonFunctionType
  /** 子组件（ProductCard 或自定义卡片） */
  children: React.ReactNode
}

/**
 * SceneShelf ProductsPanel 组件
 *
 * @description 产品面板组件，包含产品列表（Swiper）和底部操作区域（总价 + 按钮）
 */
const SceneShelfProductsPanel = React.forwardRef<HTMLDivElement, SceneShelfProductsPanelProps>(
  (
    {
      title,
      totalPriceLabel: totalPriceLabelProp,
      totalCurrentPrice: totalCurrentPriceProp,
      totalOriginalPrice: totalOriginalPriceProp,
      primaryButtonText: primaryButtonTextProp,
      primaryButtonFun: primaryButtonFunProp,
      secondaryButtonText: secondaryButtonTextProp,
      secondaryButtonFun: secondaryButtonFunProp,
      className,
      children,
      ...props
    },
    ref
  ) => {
    const context = useSceneShelfContext()
    const classNames = context?.classNames

    // Props 优先级高于 Context
    const totalPriceLabel = totalPriceLabelProp ?? context?.totalPriceLabel ?? 'Total Price:'
    const totalCurrentPrice = totalCurrentPriceProp ?? context?.totalCurrentPrice
    const totalOriginalPrice = totalOriginalPriceProp ?? context?.totalOriginalPrice
    const primaryButtonText = primaryButtonTextProp ?? context?.primaryButtonText ?? 'Shop Now'
    const primaryButtonFun = primaryButtonFunProp ?? context?.primaryButtonFun ?? 'buyNow'
    const secondaryButtonText = secondaryButtonTextProp ?? context?.secondaryButtonText ?? 'Add to Cart'
    const secondaryButtonFun = secondaryButtonFunProp ?? context?.secondaryButtonFun ?? 'addCart'

    const selectedProducts = context?.selectedProducts ?? []
    const onLearnMore = context?.onLearnMore
    const onShopNow = context?.onShopNow
    const onAddToCart = context?.onAddToCart
    const showOriginalPrice = context?.showOriginalPrice ?? true

    // 按钮 loading 状态
    const [primaryLoading, setPrimaryLoading] = React.useState(false)
    const [secondaryLoading, setSecondaryLoading] = React.useState(false)

    // 将 children 转换为数组
    const childrenArray = React.Children.toArray(children)

    // 根据按钮功能类型调用相应的回调函数
    const handleButtonClick = async (buttonFun?: ButtonFunctionType, buttonType?: 'primary' | 'secondary') => {
      if (!buttonFun) return

      const setLoading = buttonType === 'primary' ? setPrimaryLoading : setSecondaryLoading
      setLoading(true)

      try {
        switch (buttonFun) {
          case 'buyNow':
            await onShopNow?.(selectedProducts)
            break
          case 'addCart':
            await onAddToCart?.(selectedProducts)
            break
          case 'learnMore':
            await onLearnMore?.(selectedProducts)
            break
          default:
            break
        }
      } finally {
        setLoading(false)
      }
    }

    // 判断按钮是否应该被禁用（learnMore 不需要检查选中产品数量）
    const isButtonDisabled = (buttonFun?: ButtonFunctionType) => {
      if (buttonFun === 'learnMore') return false
      return selectedProducts.length === 0
    }

    return (
      <div
        ref={ref}
        className={cn(
          'bg-container-primary laptop:p-6 lg-desktop:p-8 flex min-w-0 flex-1 flex-col justify-between overflow-hidden p-4',
          className,
          classNames?.content
        )}
        {...props}
      >
        {/* 标题 */}
        {title && (
          <Heading
            as="h2"
            size={3}
            className={cn('text-info-primary flex-0 mb-[16px] line-clamp-1 shrink-0', classNames?.title)}
            html={title}
          />
        )}

        {/* 产品列表 - Swiper 容器 */}
        <div className={cn('lg-desktop:mb-[24px] mb-[16px] flex-1 ', classNames?.productList)}>
          <Swiper
            modules={[FreeMode, Mousewheel]}
            freeMode={true}
            mousewheel={{
              forceToAxis: true,
            }}
            slidesPerView="auto"
            spaceBetween={10}
            breakpoints={{
              0: {
                slidesPerView: 1.5,
              },
              768: {
                spaceBetween: 16,
                slidesPerView: 2.8,
              },
              1024: {
                slidesPerView: 2.3,
              },
              1440: {
                spaceBetween: 16,
                slidesPerView: childrenArray.length <= 3 ? 3 : 3.2,
              },
            }}
          >
            {childrenArray.map((child, index) => (
              <SwiperSlide key={index} className="h-fit shrink-0">
                {child}
              </SwiperSlide>
            ))}
          </Swiper>
        </div>

        {/* 底部区域 - 总价 + 按钮 */}
        <div
          className={cn(
            'tablet:items-end tablet:flex-row flex-0 flex flex-col items-start justify-between gap-2',
            classNames?.footer
          )}
        >
          {/* 总价 */}
          <div className={cn('flex flex-wrap items-center gap-1', classNames?.totalPrice)}>
            {totalPriceLabel && (
              <Text as="span" size={2} className="text-info-primary lg-desktop:text-[24px] text-[20px]">
                {totalPriceLabel}
              </Text>
            )}
            {totalCurrentPrice && (
              <Text
                as="span"
                size={2}
                className={cn('text-info-primary lg-desktop:text-[24px] text-[20px]', classNames?.currentTotalPrice)}
              >
                {totalCurrentPrice.formatted}
              </Text>
            )}
            {showOriginalPrice &&
              totalOriginalPrice &&
              totalCurrentPrice &&
              totalOriginalPrice.value > totalCurrentPrice.value && (
                <Text
                  as="span"
                  size={2}
                  className={cn(
                    'text-info-quaternary lg-desktop:text-[24px] text-[20px] line-through',
                    classNames?.originalTotalPrice
                  )}
                >
                  {totalOriginalPrice.formatted}
                </Text>
              )}
          </div>

          {/* 按钮组 */}
          <div className={cn('flex gap-3', classNames?.buttonGroup)}>
            {secondaryButtonText && (
              <Button
                variant="secondary"
                onClick={() => handleButtonClick(secondaryButtonFun, 'secondary')}
                disabled={isButtonDisabled(secondaryButtonFun)}
                loading={secondaryLoading}
              >
                {secondaryButtonText}
              </Button>
            )}
            {primaryButtonText && (
              <Button
                variant="primary"
                onClick={() => handleButtonClick(primaryButtonFun, 'primary')}
                disabled={isButtonDisabled(primaryButtonFun)}
                loading={primaryLoading}
              >
                {primaryButtonText}
              </Button>
            )}
          </div>
        </div>
      </div>
    )
  }
)

SceneShelfProductsPanel.displayName = 'SceneShelf.ProductsPanel'

export { SceneShelfProductsPanel }
